
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Stethoscope, MapPin, Clock, Star, Eye } from "lucide-react";
import { OptimizedImage } from "@/components/ui/optimized-image";

// Function to format price with appropriate currency
const formatPrice = (price: number, currency: string = "EUR") => {
  const currencySymbol = currency === "EUR" ? "€" : 
                         currency === "USD" ? "$" : 
                         currency === "GBP" ? "£" : 
                         currency === "FCFA" ? "FCFA" : 
                         currency === "GOURDE" ? "G" : 
                         currency;
  
  return currency === "FCFA" || currency === "GOURDE" ? 
         `${price} ${currencySymbol}` : 
         `${currencySymbol}${price}`;
};

interface ServicesGridProps {
  viewMode?: "grid" | "list";
}

export const ServicesGrid: React.FC<ServicesGridProps> = ({ viewMode = "grid" }) => {
  const navigate = useNavigate();
  
  const { data: services, isLoading } = useQuery({
    queryKey: ["services-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("professional_services")
        .select("*")
        .limit(24);
        
      if (error) throw error;
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
          <Card key={i} className="animate-pulse">
            <div className="h-28 bg-muted rounded-t-lg"></div>
            <CardHeader className="p-2">
              <div className="h-3 bg-muted rounded w-3/4"></div>
              <div className="h-2 bg-muted rounded w-1/2 mt-1"></div>
            </CardHeader>
            <CardContent className="p-2">
              <div className="h-8 bg-muted rounded"></div>
            </CardContent>
            <CardFooter className="p-2">
              <div className="h-5 bg-muted rounded w-full"></div>
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={viewMode === "grid" 
      ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2"
      : "flex flex-col gap-3"}>
      {services?.map((service) => (
        <Card 
          key={service.id} 
          className="overflow-hidden hover:shadow-md transition-all duration-300 group h-full flex flex-col"
        >
          <div className="h-0.5 w-full bg-primary/20 group-hover:bg-primary transition-colors duration-300"></div>
          
          {/* Service Image */}
          <div className="relative h-28 overflow-hidden">
            {service.photo_url ? (
              <OptimizedImage
                src={service.photo_url}
                alt={service.service_name}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-muted/10 flex items-center justify-center">
                <Stethoscope className="h-6 w-6 text-muted-foreground/40" />
              </div>
            )}
            <div className="absolute top-1 right-1 bg-primary/90 px-1.5 py-0.5 rounded-full shadow-sm backdrop-blur-sm">
              <span className="text-[10px] font-semibold text-primary-foreground">
                {service.price && formatPrice(service.price, service.currency || "EUR")}
              </span>
            </div>
          </div>
          
          <CardHeader className="p-2">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xs">{service.service_name}</CardTitle>
                <CardDescription className="flex items-center mt-0.5">
                  <Stethoscope className="h-2.5 w-2.5 mr-0.5 text-muted-foreground" />
                  <span className="text-[10px]">{service.category || "Service médical"}</span>
                </CardDescription>
              </div>
              <div className="flex items-center text-[10px] text-amber-500">
                <Star className="fill-amber-500 h-2.5 w-2.5 mr-0.5" />
                <span>{"4.8"}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="py-1 px-2 flex-grow">
            <div className="text-[10px] text-muted-foreground mb-1 line-clamp-1">
              {service.description || "Description du service non disponible."}
            </div>
            <div className="flex flex-col gap-0.5 text-[10px]">
              <div className="flex items-center">
                <MapPin className="h-2.5 w-2.5 mr-0.5 text-muted-foreground" />
                <span className="truncate">{service.address || "Emplacement non spécifié"}</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-2.5 w-2.5 mr-0.5 text-muted-foreground" />
                <span>{service.duration ? `${service.duration} min` : "Durée non spécifiée"}</span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="p-2 mt-auto">
            <Button
              className="w-full h-6 text-[10px] group-hover:bg-primary transition-colors"
              onClick={() => navigate(`/services/${service.id}`)}
            >
              <Eye className="mr-1 h-2.5 w-2.5" /> Voir le service
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};
